// @flow

import {convertLocationParamsToFetchParams} from 'app/store/sweet-api/utils';
import {
  get,
  patchJson,
  postJson,
  del,
  createErrorHandler,
} from 'app/utils/request';
import type {TeamType} from 'app/teams/types';
import {gt} from 'app/utils/i18n';
import {
  SIGNOFFTEMPLATE_API_URL,
  SIGNOFF_API_URL,
  SIGNOFF_REMIND_API_URL,
  SIGNOFF_DOCUMENT_API_URL,
  GROUPEDSIGNOFF_API_URL,
} from 'app/urls';
import {limitRequests} from 'app/utils/redux';
import type {ResponseType} from 'app/utils/redux_v2';
import {taskDispatcher} from 'app/subscriptions/taskDispatcher';

import type {
  DocumentSignoffType,
  SignoffFetchParamsType,
  SignoffLocationParamsType,
  SignoffType,
  SignoffTemplateLocationParamsType,
  SignoffTemplateFetchParamsType,
  SignoffTemplateType,
  GroupedSignoffType,
  GroupedSignoffFetchParamsType,
  GroupedSignoffLocationParamsType,
  SignoffReminderType,
} from './types';
import {showNotification} from 'app/toasts/slice';

type RequestSignoffProps = {|
  team: TeamType,
  is_trigger_on_create?: boolean,
  is_trigger_new_member?: boolean,
  is_trigger_new_document?: boolean,
  is_trigger_on_approval?: boolean,
  is_trigger_on_signoff_requested?: boolean,
  is_trigger_on_draft_approval?: boolean,
|};

const getFetchErrorMsg = () => gt('There was an error fetching the signoffs');

export const createSignoffTemplate =
  ({team, ...rules}: RequestSignoffProps) =>
  (dispatch: SimpleDispatch) =>
    dispatch(
      taskDispatcher(
        postJson(SIGNOFFTEMPLATE_API_URL, {
          team: team.url,
          ...rules,
        })
      )
    );

type UpdateSignoffProps = {|
  signofftemplate: string,
  is_trigger_new_member?: boolean,
  is_trigger_new_document?: boolean,
  is_trigger_on_approval?: boolean,
  is_trigger_on_signoff_requested?: boolean,
  is_trigger_on_draft_approval?: boolean,
|};

export const updateSignoffTemplate =
  ({signofftemplate, ...rules}: UpdateSignoffProps) =>
  (dispatch: SimpleDispatch) =>
    patchJson(signofftemplate, rules).then(
      () =>
        dispatch(showNotification(gt('Sign Off settings updated'), 'success')),
      createErrorHandler(gt('Sign Off settings failed to update'), dispatch)
    );

/* signoffs */
export const requestSignoffList = (params: SignoffFetchParamsType) =>
  get(SIGNOFF_API_URL, params);

export const postSignoffReminder = (params: SignoffReminderType) =>
  postJson(SIGNOFF_REMIND_API_URL, params);

export const RECEIVE_SIGNOFF_LIST = 'RECEIVE_SIGNOFF_LIST';

type ReceiveSignoffListType = {
  type: 'RECEIVE_SIGNOFF_LIST',
  query: SignoffFetchParamsType,
  response: ResponseType<SignoffType>,
};

export const receiveSignoffList = (
  query: SignoffFetchParamsType,
  response: ResponseType<SignoffType>
): ReceiveSignoffListType => ({
  type: RECEIVE_SIGNOFF_LIST,
  query,
  response,
});

export const fetchSignoffList = limitRequests(
  (locationParams: SignoffLocationParamsType) => (dispatch: SimpleDispatch) => {
    const fetchParams = convertLocationParamsToFetchParams(locationParams);
    return requestSignoffList(fetchParams).then(
      (response) => dispatch(receiveSignoffList(fetchParams, response)),
      createErrorHandler(getFetchErrorMsg(), dispatch)
    );
  }
);

/* document signoffs */

export const requestDocumentSignoffList = (params: SignoffFetchParamsType) =>
  get(SIGNOFF_DOCUMENT_API_URL, params);

export const RECEIVE_DOCUMENT_SIGNOFF_LIST = 'RECEIVE_DOCUMENT_SIGNOFF_LIST';

type ReceiveDocumentSignoffListType = {
  type: 'RECEIVE_DOCUMENT_SIGNOFF_LIST',
  query: SignoffFetchParamsType,
  response: ResponseType<DocumentSignoffType>,
};

export const receiveDocumentSignoffList = (
  query: SignoffFetchParamsType,
  response: ResponseType<DocumentSignoffType>
): ReceiveDocumentSignoffListType => ({
  type: RECEIVE_DOCUMENT_SIGNOFF_LIST,
  query,
  response,
});
export const fetchDocumentSignoffList = limitRequests(
  (locationParams: SignoffLocationParamsType) => (dispatch: SimpleDispatch) => {
    const fetchParams = convertLocationParamsToFetchParams(locationParams);
    return requestDocumentSignoffList(fetchParams)
      .then((response) =>
        dispatch(receiveDocumentSignoffList(fetchParams, response))
      )
      .catch(createErrorHandler(getFetchErrorMsg(), dispatch));
  }
);

/* templates */

export const requestSignoffTemplateList = (
  params: SignoffTemplateFetchParamsType
) => get(SIGNOFFTEMPLATE_API_URL, params);

export const RECEIVE_SIGNOFFTEMPLATE_LIST = 'RECEIVE_SIGNOFFTEMPLATE_LIST';

type ReceiveSignoffTemplateListType = {
  type: 'RECEIVE_SIGNOFFTEMPLATE_LIST',
  query: SignoffTemplateFetchParamsType,
  response: ResponseType<SignoffTemplateType>,
};

export const receiveSignoffTemplateList = (
  query: SignoffTemplateFetchParamsType,
  response: ResponseType<SignoffTemplateType>
): ReceiveSignoffTemplateListType => ({
  type: RECEIVE_SIGNOFFTEMPLATE_LIST,
  query,
  response,
});

export const fetchSignoffTemplateList = limitRequests(
  (locationParams: SignoffTemplateLocationParamsType) =>
    (dispatch: SimpleDispatch) => {
      const fetchParams = convertLocationParamsToFetchParams(locationParams);
      return requestSignoffTemplateList(fetchParams).then(
        (response) =>
          dispatch(receiveSignoffTemplateList(fetchParams, response)),
        createErrorHandler(getFetchErrorMsg(), dispatch)
      );
    }
);

export const fetchSignoffTemplate = (url: string) => get(url);

export const deleteSignoffTemplate =
  (url: string) => (dispatch: SimpleDispatch) =>
    del(url)
      .then(() =>
        dispatch(showNotification(gt('Sign off rules removed from team')))
      )
      .catch((error) =>
        createErrorHandler(
          gt('Unable to remove sign off rules from team'),
          dispatch
        )(error)
      );

/* grouped signoffs */

export const requestGroupedSignoffList = (
  params: GroupedSignoffFetchParamsType
) => get(GROUPEDSIGNOFF_API_URL, params);

export const RECEIVE_GROUPEDSIGNOFF_LIST = 'RECEIVE_GROUPEDSIGNOFF_LIST';

type ReceiveGroupedSignoffListType = {
  type: 'RECEIVE_GROUPEDSIGNOFF_LIST',
  query: GroupedSignoffFetchParamsType,
  response: ResponseType<GroupedSignoffType>,
};

export const receiveGroupedSignoffList = (
  query: GroupedSignoffFetchParamsType,
  response: ResponseType<GroupedSignoffType>
): ReceiveGroupedSignoffListType => ({
  type: RECEIVE_GROUPEDSIGNOFF_LIST,
  query,
  response,
});

export const fetchGroupedSignoffList = limitRequests(
  (locationParams: GroupedSignoffLocationParamsType) =>
    (dispatch: SimpleDispatch) => {
      const fetchParams = convertLocationParamsToFetchParams(locationParams);
      return requestGroupedSignoffList(fetchParams).then(
        (response) =>
          dispatch(receiveGroupedSignoffList(fetchParams, response)),
        createErrorHandler(getFetchErrorMsg(), dispatch)
      );
    }
);

export type SignoffActions =
  | ReceiveSignoffListType
  | ReceiveDocumentSignoffListType
  | ReceiveSignoffTemplateListType
  | ReceiveGroupedSignoffListType;
